/**
 * `CustomArtEditDialog` — TH-53368 / M5.E6.S2.1.
 *
 * Modal de detalhe + edição de uma custom art. Reusa o padrão do
 * `StoreFormDialog` (modal único, não drawer) por simetria com o resto
 * do admin — entrar num drawer só pra edição custaria primitivo novo
 * sem ganho real.
 *
 * Edição cobre só os campos que o PATCH /custom-arts/:id aceita
 * (TH-53366): name, active, valid_from, valid_until, store_id,
 * area_id. Tudo opcional, mas `name` é o único cuja UI bloqueia
 * envio em branco — backend rejeita string vazia.
 *
 * DELETE é destrutivo (apaga blob no MinIO); pede confirmação inline
 * via segunda click no botão (não usamos `confirm()` global porque
 * jsdom não tem). Estado de confirmação é local e reseta ao fechar.
 */

import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";

import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Select } from "@/components/ui/Select";
import type { CustomArtRead } from "@/hooks/useCreateCustomArt";
import {
  useCustomArtDelete,
  useCustomArtUpdate,
} from "@/hooks/useCustomArts";

export interface CustomArtEditOption {
  id: string;
  name: string;
}

export interface CustomArtEditDialogProps {
  art: CustomArtRead | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stores?: ReadonlyArray<CustomArtEditOption>;
  areas?: ReadonlyArray<CustomArtEditOption>;
  /** True quando o usuário tem permissão de DELETE (super_admin/tenant_admin/store_manager). */
  canDelete?: boolean;
}

/**
 * `datetime-local` aceita "YYYY-MM-DDTHH:mm" mas o backend devolve
 * ISO completo. Recortamos pro formato esperado pelo input — sem
 * tentar manter timezone porque `datetime-local` é naive de qualquer
 * jeito.
 */
function toLocalDatetimeInput(iso: string | null): string {
  if (!iso) return "";
  // Pega tudo antes do segundo separador (ex: 2026-05-15T10:30:00Z → 2026-05-15T10:30)
  const m = iso.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/);
  return m ? m[1] : "";
}

export function CustomArtEditDialog({
  art,
  open,
  onOpenChange,
  stores = [],
  areas = [],
  canDelete = false,
}: CustomArtEditDialogProps) {
  const [name, setName] = useState("");
  const [active, setActive] = useState(true);
  const [storeId, setStoreId] = useState("");
  const [areaId, setAreaId] = useState("");
  const [validFrom, setValidFrom] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const update = useCustomArtUpdate();
  const del = useCustomArtDelete();

  // Hidrata o form sempre que abre com uma arte nova — não na primeira
  // render para evitar warning de uncontrolled→controlled.
  useEffect(() => {
    if (open && art) {
      setName(art.name);
      setActive(art.active);
      setStoreId(art.store_id ?? "");
      setAreaId(art.area_id ?? "");
      setValidFrom(toLocalDatetimeInput(art.valid_from));
      setValidUntil(toLocalDatetimeInput(art.valid_until));
      setConfirmDelete(false);
      setSubmitError(null);
      update.reset();
      del.reset();
    }
    // Intencional: rodar quando `open`/`art.id` mudam; evita reset a
    // cada keypress nos campos controlados.

  }, [open, art?.id]);

  if (!art) return null;

  const disabled = update.isPending || del.isPending;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!art) return;
    setSubmitError(null);
    if (!name.trim()) {
      setSubmitError("Nome não pode ficar em branco.");
      return;
    }
    try {
      await update.mutateAsync({
        id: art.id,
        name: name.trim(),
        active,
        store_id: storeId || null,
        area_id: areaId || null,
        valid_from: validFrom || null,
        valid_until: validUntil || null,
      });
      onOpenChange(false);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Falha ao salvar.");
    }
  }

  async function onDelete() {
    if (!art) return;
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setSubmitError(null);
    try {
      await del.mutateAsync(art.id);
      onOpenChange(false);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Falha ao apagar.");
    }
  }

  const isWebUrl = /^https?:\/\//i.test(art.art_url);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogTitle>Editar arte</DialogTitle>
        <DialogDescription>
          Ajuste validade, escopo ou desative a arte. O arquivo em si não pode ser
          trocado — suba uma nova arte se precisar.
        </DialogDescription>

        <div className="mt-4 flex items-center justify-center rounded-md border border-[var(--color-border)] bg-[var(--color-background)] p-3">
          {isWebUrl ? (
            <img
              src={art.art_url}
              alt={art.name}
              className="max-h-48 max-w-full rounded object-contain"
            />
          ) : (
            <div className="flex h-32 w-full flex-col items-center justify-center gap-1 text-xs text-[var(--color-muted-foreground)]">
              <span>arte armazenada em {art.art_url.split("/")[0] || "storage"}</span>
              <span className="font-mono tabular-nums">
                {art.width}×{art.height} • {(art.file_size_bytes / 1024).toFixed(0)} KB
              </span>
            </div>
          )}
        </div>

        <form onSubmit={onSubmit} className="mt-4 space-y-4">
          <div className="grid gap-2">
            <Label htmlFor="custom-art-edit-name">Nome *</Label>
            <Input
              id="custom-art-edit-name"
              maxLength={200}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={disabled}
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              id="custom-art-edit-active"
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              disabled={disabled}
              className="h-4 w-4 rounded border-[var(--color-border-strong)] accent-[var(--color-primary)]"
            />
            <Label htmlFor="custom-art-edit-active" className="cursor-pointer">
              Arte ativa
            </Label>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="custom-art-edit-store">Loja</Label>
              <Select
                id="custom-art-edit-store"
                value={storeId}
                onChange={(e) => setStoreId(e.target.value)}
                disabled={disabled}
              >
                <option value="">Todas as lojas</option>
                {stores.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="custom-art-edit-area">Área</Label>
              <Select
                id="custom-art-edit-area"
                value={areaId}
                onChange={(e) => setAreaId(e.target.value)}
                disabled={disabled}
              >
                <option value="">Todas as áreas</option>
                {areas.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="custom-art-edit-valid-from">Válido de</Label>
              <Input
                id="custom-art-edit-valid-from"
                type="datetime-local"
                value={validFrom}
                onChange={(e) => setValidFrom(e.target.value)}
                disabled={disabled}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="custom-art-edit-valid-until">Válido até</Label>
              <Input
                id="custom-art-edit-valid-until"
                type="datetime-local"
                value={validUntil}
                onChange={(e) => setValidUntil(e.target.value)}
                disabled={disabled}
              />
            </div>
          </div>

          {submitError && (
            <div
              role="alert"
              className="rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-sm text-[var(--color-destructive)]"
            >
              {submitError}
            </div>
          )}

          <div className="flex flex-wrap justify-between gap-2 pt-2">
            {canDelete ? (
              <Button
                type="button"
                variant="ghost"
                className="text-[var(--color-destructive)] hover:bg-[var(--color-destructive)]/10"
                onClick={onDelete}
                disabled={disabled}
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
                {confirmDelete ? "Confirmar apagar" : "Apagar arte"}
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={disabled}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={disabled}>
                {update.isPending ? "Salvando…" : "Salvar"}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
